/**
 * Direct-API vendor specs. The single source of truth for every integration
 * we connect to directly (not via Truto). The generic DirectApiConnectionDrawer
 * renders a spec's `fields` and submits a JSON-encoded payload the server
 * can validate.
 */

export const DIRECT_API_SPECS = {
  horilla_self_hosted: {
    label: "Horilla (Self-Hosted)",
    heading: "Connect Horilla (Self-Hosted)",
    subtitle:
      "Pick how you want to authenticate: a long-lived access token, or a refresh token we'll use to auto-renew.",
    baseUrlDocs: {
      href: "https://docs.horilla.com/hr/technical/v2.0/doc/api/baseapi",
      label: "Click here for help regarding base URL",
    },
    alert: {
      severity: "info",
      message:
        "Horilla is self-hosted, so our server must be able to reach your portal. " +
        `Whitelist our egress IP (${
          process.env.REACT_APP_EGRESS_IP || "contact support for our egress IP"
        }) on your firewall before saving.`,
    },
    authModes: [
      {
        key: "refresh_token",
        label: "Refresh token (recommended, auto-renews)",
        description:
          "A long-lived refresh token. We create new access tokens from it automatically. If you are unsure, use this one.",
        snippet: (values) => {
          const base = (values?.base_url || "https://hr.yourcompany.com").replace(/\/$/, "");
          return `curl -X POST ${base}/api/auth/login/ \\
  -H 'Content-Type: application/json' \\
  -d '{"username":"<USERNAME>","password":"<PASSWORD>"}'`;
        },
        fields: [
          {
            name: "base_url",
            label: "Base URL",
            type: "url",
            placeholder: "https://hr.yourcompany.com",
            helper: "The address you open to sign in.",
            required: true,
          },
          {
            name: "refresh_token",
            label: "Refresh Token",
            type: "password",
            placeholder: "eyJ...",
            helper:
              "The refresh value from the login response above. We use it to create access tokens whenever the current one expires.",
            required: true,
          },
        ],
      },
      {
        key: "access_token",
        label: "Access token only",
        description:
          "A token we use as-is. When it expires you reconnect manually.",
        snippet: (values) => {
          const base = (values?.base_url || "https://hr.yourcompany.com").replace(/\/$/, "");
          return `curl -X POST ${base}/api/auth/login/ \\
  -H 'Content-Type: application/json' \\
  -d '{"username":"<USERNAME>","password":"<PASSWORD>"}'`;
        },
        fields: [
          {
            name: "base_url",
            label: "Base URL",
            type: "url",
            placeholder: "https://hr.yourcompany.com",
            helper: "The address you open to sign in.",
            required: true,
          },
          {
            name: "access_token",
            label: "Access Token",
            type: "password",
            placeholder: "eyJ...",
            helper:
              "The access value from the login response above. Use a long-lived one. When it expires you enter a new one. We cannot auto-renew in this mode.",
            required: true,
          },
        ],
      },
    ],
  },
};

export const isDirectApiIntegration = (integrationKey) =>
  Boolean(integrationKey && DIRECT_API_SPECS[integrationKey]);
